import { CalendarMonth } from '@mui/icons-material';
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined';
import ArrowDropUpOutlinedIcon from '@mui/icons-material/ArrowDropUpOutlined';
import {
    Box,
    Button,
    Container,
    Paper,
    Popover,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import dayjs, { Dayjs } from 'dayjs';
import { useLazyGetMyBookedReferralsQuery, useLazyGetMyBookingsQuery } from 'lib/features/booking/bookingService';
import { selectUser } from 'lib/features/user/userSlice';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

interface User {
    first_name: string;
    last_name: string;
}

interface ItemDetail {
    item_name: string;
}

interface Business {
    name: string;
    id: number;
}

interface Item {
    itemDetails: ItemDetail[];
    business: Business;
}

interface Referral {
    user: User;
    item: Item;
}

interface Booking {
    id: number;
    booking_time: string;
    reservation_time: string;
    customer_total: string;
    business_payout: string;
    commission_payout: string;
    platform_fee: string;
    customer_name: string;
    referral: Referral;
    state: number;
}

interface Stats {
    transactions: number;
    totalRevenue: string;
    bookingRevenue: string;
    referralRevenue: string;
}

interface StatCard {
    label: string;
    value: string | number;
}

interface ReportProps {
    businessId?: number;
    userId?: number;
}

interface SortConfig {
    key: string;
    direction: 'asc' | 'desc';
}

const ReportDetails = ({ businessId: propBusinessId, userId: propUserId }: ReportProps) => {
    const user = useSelector(selectUser) as any;
    const navigate = useNavigate();

    const { id: urlBusinessId } = useParams();

    const businessIdFromUser = user?.business_users && user.business_users.length > 0
        ? user.business_users[0]?.business_id || user.business_users[0]?.id
        : undefined;

    const businessId = propBusinessId || (urlBusinessId ? Number(urlBusinessId) : undefined) || businessIdFromUser || user?.business_id;
    const userId = propUserId || user?.id; //for testing use 75

    const [view, setView] = useState<'bookings' | 'referrals'>('bookings');
    const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'reservation_time', direction: 'desc' });
    
    // Set default date range: today to 30 days later
    // const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
    // const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().add(30, 'day'));
    const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(1, 'month').startOf('month'));
const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().subtract(1, 'month').endOf('month'));
    const [calendarAnchor, setCalendarAnchor] = useState<HTMLButtonElement | null>(null);
    const [selectionMode, setSelectionMode] = useState<'start' | 'end'>('start');

    const [getMyBookings, { data: myBookings, isLoading: isBookingsLoading }] = useLazyGetMyBookingsQuery();
    const [getMyBookedReferrals, { data: myReferrals, isLoading: isReferralsLoading }] = useLazyGetMyBookedReferralsQuery();

    useEffect(() => {
        if (user?.role_id === 4) {
            setView('bookings');
        } else {
            setView('referrals');
        }
    }, [user?.role_id]);

    // Fetch bookings
    useEffect(() => {
        const fetchBookings = async () => {
            if (user?.role_id !== 4 || !businessId) return;

            try {
                await getMyBookings({
                    business_id: Number(businessId),
                }).unwrap();
            } catch (error) {
                console.error("Error fetching bookings:", error);
            }
        };

        if (view === 'bookings') {
            fetchBookings();
        }
    }, [view, user?.role_id, businessId, getMyBookings]);

    useEffect(() => {
        const fetchReferrals = async () => {
            if (!userId) return;

            try {
                await getMyBookedReferrals({
                    user_id: Number(userId),
                }).unwrap();
            } catch (error) {
                console.error("Error fetching referrals:", error);
            }
        };

        if (view === 'referrals') {
            fetchReferrals();
        }
    }, [view, userId, getMyBookedReferrals]);

    // Get current data based on view (filtered by date range)
    const data = useMemo(() => {
        let currentData = view === 'bookings'
            ? (myBookings?.bookings || [])
            : (myReferrals?.referrals || []);

        // Filter data by date range if dates are selected
        if (startDate && endDate) {
            currentData = currentData.filter((item: Booking) => {
                const reservationDate = dayjs(item.booking_time);
                // Fixed: Use inclusive range without adding/subtracting days
                return reservationDate.isSame(startDate, 'day') || 
                       reservationDate.isSame(endDate, 'day') ||
                       (reservationDate.isAfter(startDate) && reservationDate.isBefore(endDate));
            });
        }

        return currentData;
    }, [view, myBookings, myReferrals, startDate, endDate]);

    // Sort data based on sortConfig
    const sortedData = useMemo(() => {
        if (!Array.isArray(data)) return [];

        const sortedArray = [...data];
        sortedArray.sort((a: Booking, b: Booking) => {
            let aValue: any;
            let bValue: any;

            switch (sortConfig.key) {
                case 'reservation_time':
                    aValue = new Date(a.reservation_time).getTime();
                    bValue = new Date(b.reservation_time).getTime();
                    break;
                case 'id':
                    aValue = a.id;
                    bValue = b.id;
                    break;
                case 'item_name':
                    aValue = a.referral?.item?.itemDetails?.[0]?.item_name || '';
                    bValue = b.referral?.item?.itemDetails?.[0]?.item_name || '';
                    break;
                case 'business_name':
                    aValue = a.referral?.item?.business?.name || '';
                    bValue = b.referral?.item?.business?.name || '';
                    break;
                case 'referee_name':
                    aValue = a.referral?.user ? `${a.referral.user.first_name} ${a.referral.user.last_name}` : '';
                    bValue = b.referral?.user ? `${b.referral.user.first_name} ${b.referral.user.last_name}` : '';
                    break;
                case 'customer_total':
                    aValue = parseFloat(a.customer_total || '0');
                    bValue = parseFloat(b.customer_total || '0');
                    break;
                case 'platform_fee':
                    aValue = parseFloat(a.platform_fee || '0');
                    bValue = parseFloat(b.platform_fee || '0');
                    break;
                case 'business_fee':
                    aValue = parseFloat(a.customer_total || '0') * 0.05;
                    bValue = parseFloat(b.customer_total || '0') * 0.05;
                    break;
                case 'commission_payout':
                    aValue = parseFloat(a.commission_payout || '0');
                    bValue = parseFloat(b.commission_payout || '0');
                    break;
                case 'business_payout':
                    aValue = parseFloat(a.business_payout || '0');
                    bValue = parseFloat(b.business_payout || '0');
                    break;
                case 'state':
                    aValue = a.state;
                    bValue = b.state;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });

        return sortedArray;
    }, [data, sortConfig]);

    // Format date for display
    const formatDisplayDate = (date: Dayjs | null): string => {
        if (!date) return '';
        return date.format('MMM D, YYYY');
    };

    const getDisplayText = () => {
        if (!startDate && !endDate) {
            return 'Select date range';
        }
        if (startDate && !endDate) {
            return `${formatDisplayDate(startDate)} - Select end date`;
        }
        if (startDate && endDate) {
            return `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`;
        }
        return 'Select date range';
    };

    const handleCalendarOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setCalendarAnchor(event.currentTarget);
    };

    const handleCalendarClose = () => {
        setCalendarAnchor(null);
        setSelectionMode('start');
    };

    const handleDateSelect = (date: Dayjs) => {
        if (selectionMode === 'start') {
            setStartDate(date);
            setSelectionMode('end');
        } else {
            setEndDate(date);
            if (date.isBefore(startDate!)) {
                setStartDate(date);
                setEndDate(startDate);
            }
            setCalendarAnchor(null);
            setSelectionMode('start');
        }
    };

    const clearDateRange = () => {
        setStartDate(null);
        setEndDate(null);
        setCalendarAnchor(null);
        setSelectionMode('start');
    };

    // Reset to default range (today to 30 days from now)
    // const resetToDefaultRange = () => {
    //     setStartDate(dayjs());
    //     setEndDate(dayjs().add(30, 'day'));
    //     setCalendarAnchor(null);
    // };
    const resetToDefaultRange = () => {
        setStartDate(dayjs().subtract(1, 'month').startOf('month'));
        setEndDate(dayjs().subtract(1, 'month').endOf('month'));
        setCalendarAnchor(null);
    };

    const isDateInRange = (date: Dayjs) => {
        if (!startDate || !endDate) return false;
        // Fixed: Use inclusive range without adding/subtracting days
        return date.isSame(startDate, 'day') || 
               date.isSame(endDate, 'day') ||
               (date.isAfter(startDate) && date.isBefore(endDate));
    };

    const isStartDate = (date: Dayjs) => {
        return startDate?.isSame(date, 'day') || false;
    };

    const isEndDate = (date: Dayjs) => {
        return endDate?.isSame(date, 'day') || false;
    };

    const calendarOpen = Boolean(calendarAnchor);

    const CustomDay = (props: any) => {
        const { day, ...other } = props;
        const isInRange = isDateInRange(day);
        const isStart = isStartDate(day);
        const isEnd = isEndDate(day);

        return (
            <Box
                sx={{
                    position: 'relative',
                    ...(isInRange && {
                        backgroundColor: 'rgba(50, 135, 52, 0.1)',
                    }),
                    ...(isStart && {
                        backgroundColor: '#328734',
                        color: 'white',
                        borderRadius: '50% 0 0 50%',
                    }),
                    ...(isEnd && {
                        backgroundColor: '#328734',
                        color: 'white',
                        borderRadius: '0 50% 50% 0',
                    }),
                    ...(isStart && isEnd && {
                        borderRadius: '50%',
                    }),
                }}
            >
                <PickersDay
                    {...other}
                    day={day}
                    sx={{
                        ...(isInRange && {
                            backgroundColor: 'transparent',
                        }),
                        ...(isStart && {
                            backgroundColor: '#328734',
                            color: 'white',
                            borderRadius: '50% 0 0 50%',
                            '&:hover': {
                                backgroundColor: '#2a6f2c',
                            },
                        }),
                        ...(isEnd && {
                            backgroundColor: '#328734',
                            color: 'white',
                            borderRadius: '0 50% 50% 0',
                            '&:hover': {
                                backgroundColor: '#2a6f2c',
                            },
                        }),
                        ...(isStart && isEnd && {
                            borderRadius: '50%',
                        }),
                    }}
                />
            </Box>
        );
    };

    const stats = useMemo(() => {
        if (!Array.isArray(data) || data.length === 0) {
            return {
                transactions: 0,
                totalRevenue: '0',
                bookingRevenue: '0',
                referralRevenue: '0'
            };
        }

        // const totalRevenue = data.reduce((sum: number, item: Booking) =>
        //     sum + parseFloat(item.customer_total || '0'), 0);
        const bookingRevenue = data.reduce((sum: number, item: Booking) =>
            sum + parseFloat(item.business_payout || '0'), 0);
        const referralRevenue = data.reduce((sum: number, item: Booking) =>
            sum + parseFloat(item.commission_payout || '0'), 0);
        const totalRevenue = bookingRevenue + referralRevenue;

        return {
            transactions: data.length,
            totalRevenue: totalRevenue.toFixed(2),
            bookingRevenue: bookingRevenue.toFixed(2),
            referralRevenue: referralRevenue.toFixed(2)
        };
    }, [data]);

    const handleSort = (key: string) => {
        setSortConfig(current => ({
            key,
            direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const getSortIcon = (columnKey: string) => {
        if (sortConfig.key !== columnKey) {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
                    <ArrowDropUpOutlinedIcon sx={{ fontSize: 18, mb: '-4px' }} />
                    <ArrowDropDownOutlinedIcon sx={{ fontSize: 18, mt: '-4px'}} />
                </Box>
            );
        }

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
                <ArrowDropUpOutlinedIcon 
                    sx={{ 
                        fontSize: 18, 
                        mb: '-4px', 
                        color: sortConfig.direction === 'asc' ? '#000' : '#000' 
                    }} 
                />
                <ArrowDropDownOutlinedIcon 
                    sx={{ 
                        fontSize: 18, 
                        mt: '-4px', 
                        color: sortConfig.direction === 'desc' ? '#000' : '#000' 
                    }} 
                />
            </Box>
        );
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
    };

    const formatTime = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    };

    const formatCurrency = (amount: string): string => {
        return `$${parseFloat(amount || '0').toFixed(2)}`;
    };

    const getStatus = (state: number): string => {
        return state === 4 ? 'Completed' : 'Pending';
    };

    const handleBookingIdClick = (bookingId: number) => {
        navigate(`/booking-details/${bookingId}`);
    };

    const statsData: StatCard[] = [
        { label: 'No. of Transactions', value: stats.transactions },
        { label: 'Total Revenue', value: `$${stats.totalRevenue}` },
        { label: 'Booking Revenue', value: `$${stats.bookingRevenue}` },
        { label: 'Referral Revenue', value: `$${stats.referralRevenue}` }
    ];

    const refereeStatsData: StatCard[] = [
        { label: 'No. of Transactions', value: stats.transactions },
        { label: 'Total Revenue', value: `$${stats.referralRevenue}` }
    ];

    const isLoading = isBookingsLoading || isReferralsLoading;

    const hasError = (view === 'bookings' && !businessId) || (view === 'referrals' && !userId);

    // No Data Available State (when filtered data is empty)
    const NoDataAvailableState = () => (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                textAlign: 'center',
            }}
        >
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 2, color: "#000" }}>
                No Data Available
            </Typography>
        </Box>
    );

    // Referee Empty State (role_id === 3)
    const RefereeEmptyState = () => (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
            }}
        >
            <Typography variant="h2" fontWeight="bold" sx={{ mb: 2, fontSize: '34px', color: "#000" }}>
                Welcome to Referee!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, maxWidth: 700, fontSize: '15px' }}>
                Share links, earn commissions, and watch your stats grow here.
            </Typography>
            <Button
                    variant="contained"
                    sx={{
                        textTransform: 'none',
                        px: 4,
                        py: 1,
                        fontWeight: 600,
                        borderRadius: "8px"

                    }}
                >
            
                Start Referring!
            </Button>
        </Box>
    );

    // Business Empty State (role_id === 4)
    const BusinessEmptyState = () => (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                textAlign: 'center',
            }}
        >
            <Typography variant="h2" fontWeight="bold" sx={{ mb: 2, fontSize: '34px', color: "#000" }}>
                Welcome to Referee !
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, maxWidth: 700, fontSize: '15px' }}>
                Once customers start booking through your referees, you'll see all your revenue and performance stats here.
            </Typography>
            <Box>
                <Button
                    variant="contained"
                    sx={{
                        textTransform: 'none',
                        px: 4,
                        py: 0.5,
                        fontWeight: 600,
                        borderRadius: "8px"

                    }}
                >
                    Add Experience
                </Button>
                <Button
                    sx={{
                        border: '1px solid', ml: 2, borderRadius: "8px", py: 0.5, px: 4, color: '#000',

                    }}>
                    Invite Referees
                </Button>
            </Box>

        </Box>
    );

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    if (hasError) {
        return (
            <Box>
                <Typography>Error loading data</Typography>
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ py: 4 }}>
                <Container maxWidth="xl">
                    <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                        REPORTS
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Year to Date
                    </Typography>

                    {/* Stats Cards - Always show even when no data */}
                    <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: user?.role_id === 3 ? 'repeat(auto-fit, minmax(200px, 1fr))' : 'repeat(auto-fit, minmax(200px, 1fr))', 
                        gap: 2, 
                        mb: 4, 
                        border: "1px solid #7EE556", 
                        borderRadius: "12px", 
                        boxShadow: "-2px 9px 2px -4px rgba(126,229,86,0.2)", 
                        maxWidth: "90%", 
                        padding: "28px 0px" 
                    }}>
                        {(user?.role_id === 3 ? refereeStatsData : statsData).map((stat, index, array) => (
                            <Paper key={index} elevation={0} sx={{ 
                                px: 2.5, 
                                borderRadius: 0, 
                                background: "transparent", 
                                borderRight: index < array.length - 1 ? "1px solid #7EE556" : "none" 
                            }}>
                                <Typography variant="body2" color="#000" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                                    {stat.label}
                                </Typography>
                                <Typography variant="h5" fontWeight="bold" sx={{ lineHeight: "28px", fontSize: "26px" }}>
                                    {stat.value}
                                </Typography>
                            </Paper>
                        ))}
                    </Box>

                    {/* Table Section - Always show header, conditionally show body */}
                    <Paper elevation={0} sx={{ border: 0, borderRadius: 2, overflow: 'hidden', background: "transparent", px: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2, borderBottom: '1px solid #e0e0e0' }}>
                            <ToggleButtonGroup
                                value={view}
                                exclusive
                                onChange={(_e, newView: 'bookings' | 'referrals' | null) => newView && setView(newView)}
                                sx={{
                                    '& .MuiToggleButton-root': {
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        border: 'none',
                                        '&.Mui-selected': {
                                            bgcolor: 'transparent',
                                            borderRadius: 0,
                                            borderBottom: "1px solid #000",
                                        }
                                    }
                                }}
                            >
                                {user?.role_id === 4 && (
                                    <ToggleButton value="bookings" sx={{ px: "10px", py: "0", borderRight: "1px solid #000", marginRight: "10px" }}>
                                        <Box sx={{ p: 0 }}>Bookings</Box>
                                    </ToggleButton>
                                )}
                                <ToggleButton value="referrals" sx={{ p: 0, px: "15px" }}>Referrals</ToggleButton>
                            </ToggleButtonGroup>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<CalendarMonth />}
                                    onClick={handleCalendarOpen}
                                    sx={{
                                        textTransform: 'none',
                                        borderColor: '#d0d0d0',
                                        color: 'text.primary',
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    {getDisplayText()}
                                </Button>
                            </Box>
                        </Box>
                        
                        {/* Show table headers always, conditionally show body or empty state */}
                        <Box sx={{ border: "1px solid #E0E0E0" }}>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#F2F8F3' }}>
                                            {/* Date/Time Header */}
                                            <TableCell 
                                                sx={{ borderRight: "1px solid #E0E0E0", padding: '16px', cursor: 'pointer' }}
                                                onClick={() => handleSort('booking_time')}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, fontSize: '13px' }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                                                        <span>Date/</span>
                                                        <span>Time</span>
                                                    </Box>
                                                    {getSortIcon('booking_time')}
                                                </Box>
                                            </TableCell>

                                            {/* Booking ID Header */}
                                            <TableCell 
                                                sx={{ borderRight: "1px solid #E0E0E0", padding: '16px', cursor: 'pointer' }}
                                                onClick={() => handleSort('id')}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, fontSize: '13px' }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                                                        <span>Book</span>
                                                        <span>ing ID</span>
                                                    </Box>
                                                    {getSortIcon('id')}
                                                </Box>
                                            </TableCell>

                                            {/* Item Name Header */}
                                            <TableCell 
                                                sx={{ borderRight: "1px solid #E0E0E0", padding: '16px', cursor: 'pointer' }}
                                                onClick={() => handleSort('item_name')}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, fontSize: '13px' }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                                                        <span>Item</span>
                                                        <span>Name</span>
                                                    </Box>
                                                    {getSortIcon('item_name')}
                                                </Box>
                                            </TableCell>

                                            {/* Business/Referee Header */}
                                            <TableCell 
                                                sx={{ borderRight: "1px solid #E0E0E0", padding: '16px', cursor: 'pointer' }}
                                                onClick={() => handleSort(user?.role_id === 3 ? 'business_name' : (view === 'referrals' ? 'business_name' : 'referee_name'))}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, fontSize: '13px' }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                                                        {user?.role_id === 3 ? 'Business' : (view === 'referrals' ? 'Business' : 'Referee')}
                                                    </Box>
                                                    {getSortIcon(user?.role_id === 3 ? 'business_name' : (view === 'referrals' ? 'business_name' : 'referee_name'))}
                                                </Box>
                                            </TableCell>

                                            {/* Gross Header */}
                                            <TableCell 
                                                sx={{ borderRight: "1px solid #E0E0E0", padding: '16px', cursor: 'pointer' }}
                                                onClick={() => handleSort('customer_total')}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, fontSize: '13px' }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                                                        Gross
                                                    </Box>
                                                    {getSortIcon('customer_total')}
                                                </Box>
                                            </TableCell>

                                            {/* Additional headers for business view */}
                                            {user?.role_id === 4 && view === 'bookings' && (
                                                <TableCell 
                                                    sx={{ borderRight: "1px solid #E0E0E0", padding: '16px', cursor: 'pointer' }}
                                                    onClick={() => handleSort('business_fee')}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, fontSize: '13px' }}>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                                                            Fee
                                                        </Box>
                                                        {getSortIcon('business_fee')}
                                                    </Box>
                                                </TableCell>
                                            )}

                                            {/* Commission Header */}
                                            <TableCell 
                                                sx={{ borderRight: "1px solid #E0E0E0", padding: '16px', cursor: 'pointer' }}
                                                onClick={() => handleSort('commission_payout')}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, fontSize: '13px' }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                                                        Commission
                                                    </Box>
                                                    {getSortIcon('commission_payout')}
                                                </Box>
                                            </TableCell>

                                            {/* Net Payout Header (only for business bookings view) */}
                                            {user?.role_id === 4 && view === 'bookings' && (
                                                <TableCell 
                                                    sx={{ borderRight: "1px solid #E0E0E0", padding: '16px', cursor: 'pointer' }}
                                                    onClick={() => handleSort('business_payout')}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, fontSize: '13px' }}>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                                                            <span>Net</span>
                                                            <span>Payout</span>
                                                        </Box>
                                                        {getSortIcon('business_payout')}
                                                    </Box>
                                                </TableCell>
                                            )}

                                            {/* Status Header */}
                                            <TableCell 
                                                sx={{ padding: '16px', cursor: 'pointer' }}
                                                onClick={() => handleSort('state')}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, fontSize: '13px' }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                                                        Status
                                                    </Box>
                                                    {getSortIcon('state')}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    
                                    {/* Conditionally render table body or empty state */}
                                    {data.length > 0 ? (
                                        <TableBody>
                                            {sortedData.map((booking: Booking, index: number) => (
                                                <TableRow key={index} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                                                    <TableCell sx={{ fontSize: '15px', fontWeight: 700, borderRight: "1px solid #E0E0E0", padding: '16px' }}>
                                                        <Box sx={{ fontSize: '15px', fontWeight: 700 }}>{formatDate(booking.booking_time)}</Box>
                                                        <Box sx={{ fontSize: '15px', fontWeight: 700 }}>{formatTime(booking.booking_time)}</Box>
                                                    </TableCell>
                                                    <TableCell 
                                                        sx={{ 
                                                            fontSize: '15px', 
                                                            fontWeight: 700, 
                                                            borderRight: "1px solid #E0E0E0", 
                                                            padding: '16px',
                                                            cursor: 'pointer',
                                                            textDecoration: 'underline',
                                                        }}
                                                        onClick={() => handleBookingIdClick(booking.id)}
                                                    >
                                                        #{booking.id}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '15px', fontWeight: 700, borderRight: "1px solid #E0E0E0", padding: '16px' }}>
                                                        {booking.referral?.item?.itemDetails?.[0]?.item_name || 'N/A'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '15px', fontWeight: 700, borderRight: "1px solid #E0E0E0", padding: '16px' }}>
                                                        {user?.role_id === 3
                                                            ? (booking.referral?.item?.business?.name || 'N/A')
                                                            : view === 'referrals'
                                                                ? (booking.referral?.item?.business?.name || 'N/A')
                                                                : booking.referral?.user
                                                                    ? `${booking.referral.user.first_name} ${booking.referral.user.last_name.charAt(0)}.`
                                                                    : 'N/A'
                                                        }
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '15px', fontWeight: 700, borderRight: "1px solid #E0E0E0", padding: '16px' }}>
                                                        {formatCurrency(booking.customer_total)}
                                                    </TableCell>
                                                    {user?.role_id === 4 && view === 'bookings' && (
                                                        <TableCell sx={{ fontSize: '15px', fontWeight: 700, borderRight: "1px solid #E0E0E0", padding: '16px' }}>
                                                            -{formatCurrency(String(Number(booking.customer_total) * 0.05))}
                                                        </TableCell>
                                                    )}
                                                    <TableCell sx={{ fontSize: '15px', fontWeight: 700, borderRight: "1px solid #E0E0E0", padding: '16px' }}>
                                                        -{formatCurrency(booking.commission_payout)}
                                                    </TableCell>
                                                    {user?.role_id === 4 && view === 'bookings' && (
                                                        <TableCell sx={{ fontSize: '15px', fontWeight: 700, borderRight: "1px solid #E0E0E0", padding: '16px' }}>
                                                            {formatCurrency(booking.business_payout)}
                                                        </TableCell>
                                                    )}
                                                    <TableCell sx={{ padding: '16px' }}>
                                                        <Typography
                                                            sx={{
                                                                fontSize: '15px', fontWeight: 700,
                                                            }}
                                                        >
                                                            {getStatus(booking.state)}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    ) : (
                                        <TableBody>
                                            <TableRow>
                                                <TableCell colSpan={user?.role_id === 4 && view === 'bookings' ? 9 : 7} sx={{ py: 8, textAlign: 'center' }}>
                                                    <NoDataAvailableState />
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    )}
                                </Table>
                            </TableContainer>
                        </Box>
                    </Paper>

                    {/* Calendar Popover */}
                    <Popover
                        id={calendarOpen ? 'date-range-popover' : undefined}
                        open={calendarOpen}
                        anchorEl={calendarAnchor}
                        onClose={handleCalendarClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        sx={{
                            '& .MuiPopover-paper': {
                                borderRadius: 2,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                            }
                        }}
                    >
                        <Box sx={{ p: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                Select Date Range
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {selectionMode === 'start' ? 'Select start date' : 'Select end date'}
                            </Typography>
                            <DateCalendar
                                value={selectionMode === 'start' ? startDate : endDate}
                                onChange={handleDateSelect}
                                slots={{
                                    day: CustomDay,
                                }}
                                sx={{
                                    '& .MuiPickersCalendarHeader-root': {
                                        marginTop: 0,
                                    }
                                }}
                            />
                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                <Button
                                    size="small"
                                    onClick={resetToDefaultRange}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Default Range
                                </Button>
                                <Box sx={{ flex: 1 }} />
                                <Button
                                    size="small"
                                    onClick={clearDateRange}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Clear
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={handleCalendarClose}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    </Popover>
                </Container>
            </Box>
        </LocalizationProvider>
    );
}

export default ReportDetails;